import React, { useState, useEffect, useCallback } from 'react';
import { airtableService } from '../services/airtable';

const GUARDS = [
  'Shawn Hayes',
  'Federico Irizarry',
  'Timothy Maisonneuve',
  'Gregory Gooden',
  'Lewis Finlay',
];

const PatrolApp = () => {
  const [activeTab, setActiveTab] = useState('vehicle');

  // ── Vehicle state ─────────────────────────────────────────────────────────────
  const [searchMode, setSearchMode] = useState('pass'); // 'pass' | 'plate'
  const [query, setQuery] = useState('');
  const [vehicleResult, setVehicleResult] = useState(null); // null=empty, undefined=loading, false=notfound, obj=found
  const [vehicleCacheInfo, setVehicleCacheInfo] = useState(null);
  const [loadingVehicleDB, setLoadingVehicleDB] = useState(false);

  // ── Unauthorized vehicles state ───────────────────────────────────────────────
  const [unauthPlate, setUnauthPlate] = useState('');
  const [unauthLocation, setUnauthLocation] = useState('');
  const [unauthOfficer, setUnauthOfficer] = useState('');
  const [unauthNotes, setUnauthNotes] = useState('');
  const [unauthSubmitting, setUnauthSubmitting] = useState(false);
  const [unauthSuccess, setUnauthSuccess] = useState('');
  const [unauthError, setUnauthError] = useState('');
  const [unauthVehicles, setUnauthVehicles] = useState([]);
  const [unauthLoading, setUnauthLoading] = useState(true);
  const [clearingId, setClearingId] = useState(null);

  // ── Perimeter state ───────────────────────────────────────────────────────────
  const [perimeterChecks, setPerimeterChecks] = useState([]);
  const [perimeterForm, setPerimeterForm] = useState({ guard: '', status: '', notes: '', showForm: false });

  // ── Incident state ────────────────────────────────────────────────────────────
  const [incidentForm, setIncidentForm] = useState({ location: '', description: '', priority: 'Medium', guard: '' });

  useEffect(() => {
    loadPerimeterChecks();
    loadVehicleDatabase();
  }, []);

  // Declared before the effect below so it can be listed as a dependency —
  // dependency arrays are evaluated during render, so a `const` defined later
  // would still be in its temporal dead zone here.
  const loadUnauthVehicles = useCallback(async () => {
    setUnauthLoading(true);
    const data = await airtableService.getUnauthorizedVehicles();
    setUnauthVehicles(data);
    setUnauthLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'unauthorized') loadUnauthVehicles();
  }, [activeTab, loadUnauthVehicles]);

  // ── Vehicle DB ────────────────────────────────────────────────────────────────

  const loadVehicleDatabase = async () => {
    setLoadingVehicleDB(true);
    await airtableService.loadVehicleDatabase();
    setVehicleCacheInfo(airtableService.getCacheInfo());
    setLoadingVehicleDB(false);
  };

  const searchVehicle = async () => {
    if (!query.trim()) return;
    setVehicleResult(undefined);
    const result = searchMode === 'plate'
      ? await airtableService.searchVehicle(query)
      : await airtableService.searchVehicleByPass(query);
    setVehicleResult(result ?? false);
  };

  const switchMode = (mode) => {
    setSearchMode(mode);
    setQuery('');
    setVehicleResult(null);
  };

  // ── Unauthorized vehicles ─────────────────────────────────────────────────────

  const submitUnauthVehicle = async () => {
    if (!unauthPlate.trim() || !unauthLocation.trim() || !unauthOfficer) {
      setUnauthError('License plate, location, and officer are required.');
      return;
    }
    setUnauthError('');
    setUnauthSubmitting(true);
    const ok = await airtableService.reportUnauthorizedVehicle({
      licensePlate: unauthPlate,
      location: unauthLocation,
      reportedBy: unauthOfficer,
      notes: unauthNotes,
    });
    setUnauthSubmitting(false);
    if (ok) {
      setUnauthSuccess(`${unauthPlate.trim().toUpperCase()} logged as unauthorized.`);
      setUnauthPlate(''); setUnauthLocation(''); setUnauthNotes('');
      setTimeout(() => setUnauthSuccess(''), 5000);
      loadUnauthVehicles();
    } else {
      setUnauthError('Failed to submit. Please try again.');
    }
  };

  const clearUnauthVehicle = async (recordId, plate) => {
    setClearingId(recordId);
    const result = await airtableService.clearUnauthorizedVehicle(recordId, plate);
    setClearingId(null);
    if (result === 'cleared') {
      loadUnauthVehicles();
    } else if (result === 'not-authorized') {
      alert(`${plate} is not in the authorized vehicle database. Add it to Airtable first.`);
    } else {
      alert('Error clearing vehicle. Please try again.');
    }
  };

  // ── Perimeter ─────────────────────────────────────────────────────────────────

  const loadPerimeterChecks = async () => {
    const data = await airtableService.getPerimeterChecks();
    setPerimeterChecks(data);
  };

  const startPerimeterCheck = (status) => {
    setPerimeterForm({ guard: '', status, notes: '', showForm: true });
  };

  const submitPerimeterCheck = async () => {
    if (!perimeterForm.guard) { alert('Please select your name'); return; }
    if (perimeterForm.status === 'Issue Found' && !perimeterForm.notes) {
      alert('Please describe the issue found'); return;
    }
    const result = await airtableService.createPerimeterCheck({
      officer: perimeterForm.guard,
      status: perimeterForm.status,
      notes: perimeterForm.notes,
    });
    if (result) {
      alert('Perimeter check logged successfully!');
      setPerimeterForm({ guard: '', status: '', notes: '', showForm: false });
      loadPerimeterChecks();
    } else {
      alert('Error logging perimeter check. Please try again.');
    }
  };

  // ── Incident ──────────────────────────────────────────────────────────────────

  const submitIncident = async () => {
    if (!incidentForm.location.trim() || !incidentForm.description.trim() || !incidentForm.guard.trim()) {
      alert('Please fill in all required fields'); return;
    }
    try {
      await airtableService.createIncident(incidentForm);
      alert('Incident reported successfully!');
      setIncidentForm({ location: '', description: '', priority: 'Medium', guard: '' });
    } catch (error) {
      alert(`Error reporting incident: ${error.message}`);
    }
  };

  // ── Shared helpers ────────────────────────────────────────────────────────────

  const formatDateTime = (str) => {
    if (!str) return '—';
    try {
      return new Date(str).toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const tabs = [
    { id: 'vehicle', label: 'Vehicle Check' },
    { id: 'unauthorized', label: 'Unauthorized' },
    { id: 'perimeter', label: 'Perimeter Check' },
    { id: 'incident', label: 'Report Incident' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Tab Navigation */}
      <div className="flex bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
        {tabs.map((t, i) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-4 px-4 text-center font-medium text-sm ${
              i === 0 ? 'rounded-l-lg' : i === tabs.length - 1 ? 'rounded-r-lg' : ''
            } ${
              activeTab === t.id
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Vehicle Check ── */}
      {activeTab === 'vehicle' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-6">Vehicle Verification</h2>

          {/* DB Status */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-blue-800">Vehicle Database</h3>
                {loadingVehicleDB ? (
                  <p className="text-sm text-blue-600">Loading…</p>
                ) : vehicleCacheInfo ? (
                  <div className="text-sm text-blue-600">
                    <p>✅ {vehicleCacheInfo.vehicleCount} vehicles · {vehicleCacheInfo.isStale ? '⚠ Stale' : 'Up to date'}</p>
                    <p className="text-xs">Last synced: {vehicleCacheInfo.lastUpdated.toLocaleString()}</p>
                    <p className="text-xs">Available offline ✓</p>
                  </div>
                ) : (
                  <p className="text-sm text-orange-600">⚠️ Database not loaded</p>
                )}
              </div>
              <button
                onClick={loadVehicleDatabase}
                disabled={loadingVehicleDB}
                className={`px-4 py-2 rounded-lg font-medium ${
                  loadingVehicleDB ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {loadingVehicleDB ? 'Loading…' : 'Sync'}
              </button>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
            {[['pass', 'Parking Pass'], ['plate', 'License Plate']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => switchMode(id)}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all ${
                  searchMode === id ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search input */}
          <div className="flex gap-3 mb-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              placeholder={searchMode === 'plate' ? 'e.g. LCY4547' : 'e.g. CKK-0002'}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && searchVehicle()}
            />
            <button
              onClick={searchVehicle}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
            >
              Search
            </button>
            {query && (
              <button
                onClick={() => { setQuery(''); setVehicleResult(null); }}
                className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Clear
              </button>
            )}
          </div>
          {searchMode === 'pass' && (
            <p className="text-xs text-gray-400 mb-4 italic">Hyphens and spaces are ignored — "ckk0002" finds CKK-0002.</p>
          )}

          {/* Results */}
          {vehicleResult === undefined && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-700 font-medium">🔍 Searching…</p>
            </div>
          )}
          {vehicleResult === false && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800">❌ Not Authorized</h3>
              <p className="text-sm text-red-600 mt-1">"{query}" not found in the authorized vehicle database.</p>
            </div>
          )}
          {vehicleResult && vehicleResult !== false && vehicleResult !== undefined && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3">✅ Authorized Vehicle</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {vehicleResult.fields['Parking Pass Number'] && (
                  <div><strong>Pass:</strong> {vehicleResult.fields['Parking Pass Number']}</div>
                )}
                <div><strong>Plate:</strong> {vehicleResult.fields['License Plate']}</div>
                {vehicleResult.fields.Make && <div><strong>Make:</strong> {vehicleResult.fields.Make}</div>}
                {vehicleResult.fields.Model && <div><strong>Model:</strong> {vehicleResult.fields.Model}</div>}
                {vehicleResult.fields.Color && <div><strong>Color:</strong> {vehicleResult.fields.Color}</div>}
                {vehicleResult.fields['Owner Name'] && <div><strong>Owner:</strong> {vehicleResult.fields['Owner Name']}</div>}
              </div>
              {vehicleResult.fields.Notes && (
                <div className="mt-3 text-sm"><strong>Notes:</strong> {vehicleResult.fields.Notes}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Unauthorized Vehicles ── */}
      {activeTab === 'unauthorized' && (
        <div className="space-y-6">
          {/* Report form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-6">Report Unauthorized Vehicle</h2>

            {unauthSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 mb-4 font-medium">
                ✅ {unauthSuccess}
              </div>
            )}
            {unauthError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4">
                {unauthError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">License Plate *</label>
                <input
                  type="text"
                  value={unauthPlate}
                  onChange={(e) => setUnauthPlate(e.target.value.toUpperCase())}
                  placeholder="e.g. ABC1234"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location *</label>
                <input
                  type="text"
                  value={unauthLocation}
                  onChange={(e) => setUnauthLocation(e.target.value)}
                  placeholder="Parking lot A, Main entrance, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Reporting Officer *</label>
                <select
                  value={unauthOfficer}
                  onChange={(e) => setUnauthOfficer(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select your name…</option>
                  {GUARDS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes (optional)</label>
                <textarea
                  value={unauthNotes}
                  onChange={(e) => setUnauthNotes(e.target.value)}
                  rows={2}
                  placeholder="Vehicle description, reason for concern, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={submitUnauthVehicle}
                disabled={unauthSubmitting}
                className={`w-full py-3 rounded-lg font-medium text-lg text-white ${
                  unauthSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'
                }`}
              >
                {unauthSubmitting ? 'Submitting…' : 'Log Unauthorized Vehicle'}
              </button>
            </div>
          </div>

          {/* Active list */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Active Unauthorized Vehicles
                {unauthVehicles.length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                    {unauthVehicles.length}
                  </span>
                )}
              </h2>
              <button onClick={loadUnauthVehicles} className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">
                Refresh
              </button>
            </div>

            {unauthLoading ? (
              <p className="text-gray-500 text-center py-6">Loading…</p>
            ) : unauthVehicles.length === 0 ? (
              <p className="text-gray-400 text-center py-6">No active unauthorized vehicles.</p>
            ) : (
              <div className="space-y-3">
                {unauthVehicles.map(v => (
                  <div key={v.id} className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-2xl font-black tracking-widest text-gray-900">
                        {v.fields['License Plate']}
                      </span>
                      <span className="text-xs text-gray-400">{formatDateTime(v.fields['Date/Time'])}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">📍 {v.fields.Location}</p>
                    <p className="text-sm text-gray-500 mb-2">Officer: {v.fields['Reported By']}</p>
                    {v.fields.Notes && <p className="text-sm text-gray-500 italic mb-2">{v.fields.Notes}</p>}
                    <button
                      onClick={() => clearUnauthVehicle(v.id, v.fields['License Plate'])}
                      disabled={clearingId === v.id}
                      className={`w-full py-2 rounded-lg text-sm font-semibold text-white ${
                        clearingId === v.id ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {clearingId === v.id ? 'Checking…' : 'Clear (verify in authorized DB)'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Perimeter Check ── */}
      {activeTab === 'perimeter' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Perimeter Checks</h2>
            <div className="flex gap-3">
              <button onClick={() => startPerimeterCheck('Complete')} disabled={perimeterForm.showForm}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                Log Complete Check
              </button>
              <button onClick={() => startPerimeterCheck('Issue Found')} disabled={perimeterForm.showForm}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                Report Issue
              </button>
              <button onClick={loadPerimeterChecks} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                Refresh
              </button>
            </div>
          </div>

          {perimeterForm.showForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 space-y-4">
              <h3 className="text-lg font-bold">
                {perimeterForm.status === 'Complete' ? 'Log Complete Check' : 'Report Issue Found'}
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2">Security Guard *</label>
                <select value={perimeterForm.guard}
                  onChange={(e) => setPerimeterForm({...perimeterForm, guard: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">Select your name…</option>
                  {GUARDS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {perimeterForm.status === 'Issue Found' ? 'Describe the Issue *' : 'Additional Notes (optional)'}
                </label>
                <textarea value={perimeterForm.notes}
                  onChange={(e) => setPerimeterForm({...perimeterForm, notes: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={perimeterForm.status === 'Issue Found' ? 'Describe what you found…' : 'Any additional observations…'} />
              </div>
              <div className="flex gap-3">
                <button onClick={submitPerimeterCheck}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium">
                  Submit Check
                </button>
                <button onClick={() => setPerimeterForm({ guard: '', status: '', notes: '', showForm: false })}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {perimeterChecks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No perimeter checks recorded yet.</p>
            ) : perimeterChecks.map((check) => (
              <div key={check.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">Check #{check.fields['Check ID'] || check.id.slice(-4)}</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    check.fields['Completion Status'] === 'Complete' ? 'bg-green-100 text-green-800' :
                    check.fields['Completion Status'] === 'Issue Found' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {check.fields['Completion Status'] || 'Pending'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>Officer: {check.fields['Security Guard Name']}</div>
                  <div>Time: {formatDateTime(check.fields['Check Time'])}</div>
                </div>
                {check.fields.Notes && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm"><strong>Notes:</strong> {check.fields.Notes}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Report Incident ── */}
      {activeTab === 'incident' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-6">Report Incident</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Security Guard *</label>
              <select value={incidentForm.guard}
                onChange={(e) => setIncidentForm({...incidentForm, guard: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Select guard name…</option>
                {GUARDS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Location/Area *</label>
              <input type="text" value={incidentForm.location}
                onChange={(e) => setIncidentForm({...incidentForm, location: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Main entrance, cafeteria, gymnasium, etc." />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select value={incidentForm.priority}
                onChange={(e) => setIncidentForm({...incidentForm, priority: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea value={incidentForm.description}
                onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Detailed description of the incident…" />
            </div>
            <button onClick={submitIncident}
              className="w-full py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium text-lg">
              Submit Incident Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrolApp;
